/**
 * =============================================================================
 * SPEQ55 RFP Pro - Authentication Context
 * =============================================================================
 *
 * This module provides React Context for managing authentication state
 * throughout the application. It handles:
 *
 * - User authentication state (logged in/out)
 * - Access and refresh token management
 * - Automatic token refresh before expiration
 * - User profile data
 * - Login, logout, and session management
 *
 * WHY USE CONTEXT FOR AUTH?
 * -------------------------
 * 1. Global State: Auth state is needed throughout the app
 * 2. Prop Drilling Prevention: Avoid passing user down every component tree
 * 3. Centralized Logic: All auth logic in one place
 * 4. Easy Testing: Can mock the context in tests
 *
 * HOW IT WORKS:
 * -------------
 * 1. On app load, check for stored tokens in localStorage
 * 2. If tokens exist, verify them with the server (GET /api/auth/me)
 * 3. If valid, restore the session; if not, clear stored tokens
 * 4. Provide login/logout functions to all components
 * 5. Automatically refresh access token before it expires
 *
 * TOKEN STORAGE:
 * --------------
 * - Tokens are stored in localStorage for persistence
 * - Access token: 'auth_token' (short-lived, 15 min)
 * - Refresh token: 'refresh_token' (long-lived, 7 days)
 * - User data: Stored in Context state (not localStorage)
 *
 * SECURITY CONSIDERATIONS:
 * ------------------------
 * - localStorage is vulnerable to XSS attacks
 * - For higher security, consider HttpOnly cookies (requires backend changes)
 * - Token refresh runs before expiration to maintain seamless UX
 * - All tokens are cleared on logout
 *
 * USAGE:
 * ------
 * // In component
 * import { useAuth } from '../contexts/AuthContext';
 *
 * function MyComponent() {
 *   const { user, isAuthenticated, login, logout } = useAuth();
 *
 *   if (!isAuthenticated) return <LoginPrompt />;
 *
 *   return <div>Hello, {user.firstName}!</div>;
 * }
 *
 * @module contexts/AuthContext
 * @author SPEQ55
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import api from '../api/client';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * localStorage keys for token storage.
 * Using constants prevents typos and makes it easy to change.
 */
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
};

/**
 * Time before token expiration to trigger refresh (in milliseconds).
 * Refreshing 1 minute before expiration provides buffer for network latency.
 */
const REFRESH_THRESHOLD_MS = 60 * 1000; // 1 minute

/**
 * Access token lifetime (must match backend configuration).
 * Used to schedule token refresh.
 */
const _ACCESS_TOKEN_LIFETIME_MS = 15 * 60 * 1000; // 15 minutes

// =============================================================================
// CONTEXT CREATION
// =============================================================================

/**
 * Authentication context.
 * Initialized with null - must be provided via AuthProvider.
 *
 * We use null as default to catch cases where components try to
 * use the context without being wrapped in AuthProvider.
 */
const AuthContext = createContext(null);

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Safely parses a JWT token and extracts the payload.
 *
 * JWTs have three parts: header.payload.signature
 * We only need the payload to check expiration.
 *
 * @param {string} token - JWT token string
 * @returns {Object|null} Decoded payload or null if invalid
 */
function parseJWT(token) {
  try {
    // Split the token into parts
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    // Base64 URL encoding uses - and _ instead of + and /
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));

    return payload;
  } catch {
    return null;
  }
}

/**
 * Checks if a JWT token is expired or about to expire.
 *
 * @param {string} token - JWT token string
 * @param {number} [thresholdMs=0] - Time before expiration to consider expired
 * @returns {boolean} True if token is expired or will expire within threshold
 */
function isTokenExpired(token, thresholdMs = 0) {
  const payload = parseJWT(token);

  if (!payload || !payload.exp) {
    // If we can't determine expiration, assume expired
    return true;
  }

  // exp is in seconds, Date.now() is in milliseconds
  const expirationMs = payload.exp * 1000;
  const nowMs = Date.now();

  return nowMs >= expirationMs - thresholdMs;
}

/**
 * Gets stored tokens from localStorage.
 *
 * @returns {Object} Object containing accessToken and refreshToken (may be null)
 */
function getStoredTokens() {
  return {
    accessToken: localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
    refreshToken: localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
  };
}

/**
 * Stores tokens in localStorage.
 *
 * @param {string} accessToken - JWT access token
 * @param {string} refreshToken - JWT refresh token
 */
function storeTokens(accessToken, refreshToken) {
  if (accessToken) {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  }
  if (refreshToken) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  }
}

/**
 * Clears all stored tokens from localStorage.
 * Called on logout or when tokens are invalid.
 */
function clearStoredTokens() {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
}

// =============================================================================
// AUTH PROVIDER COMPONENT
// =============================================================================

/**
 * Authentication Provider component.
 *
 * This component wraps the entire application (or parts that need auth)
 * and provides authentication state and methods to all children.
 *
 * INITIALIZATION FLOW:
 * 1. Component mounts, isLoading is true
 * 2. Check localStorage for existing tokens
 * 3. If tokens exist, validate with server (GET /api/auth/me)
 * 4. If valid, restore session; if invalid, clear tokens
 * 5. Set isLoading to false, render children
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap
 */
export function AuthProvider({ children }) {
  // ==========================================================================
  // STATE
  // ==========================================================================

  /**
   * Current authenticated user object.
   * null when not authenticated.
   */
  const [user, setUser] = useState(null);

  /**
   * Loading state during authentication check.
   * true during initial load and during login/logout operations.
   * Use this to show loading indicators.
   */
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Error state for authentication operations.
   * Contains error message string or null.
   */
  const [error, setError] = useState(null);

  /**
   * Timer ID for token refresh.
   * Used to cancel scheduled refresh on logout.
   */
  const [refreshTimer, setRefreshTimer] = useState(null);

  // ==========================================================================
  // TOKEN REFRESH
  // ==========================================================================

  /**
   * Refreshes the access token using the refresh token.
   *
   * This function:
   * 1. Gets the refresh token from storage
   * 2. Calls the refresh endpoint
   * 3. Stores the new access token
   * 4. Updates user state if needed
   * 5. Schedules the next refresh
   *
   * @returns {Promise<boolean>} True if refresh succeeded
   */
  const refreshAccessToken = useCallback(async () => {
    try {
      const { refreshToken } = getStoredTokens();

      // Guard clause: No refresh token available
      if (!refreshToken) {
        return false;
      }

      // Call the refresh endpoint
      const response = await api.post('/auth/refresh', { refreshToken });

      // Check for successful response
      if (response.data?.success && response.data?.accessToken) {
        // Store the new access token
        storeTokens(response.data.accessToken, null);

        // Update user state if provided
        if (response.data.user) {
          setUser(response.data.user);
        }

        return true;
      }

      return false;
    } catch {
      // Refresh failed - token may be expired or revoked
      clearStoredTokens();
      setUser(null);

      return false;
    }
  }, []);

  /**
   * Schedules automatic token refresh before expiration.
   *
   * Calculates when the token will expire and schedules a refresh
   * to occur 1 minute before that time.
   *
   * @param {string} accessToken - Current access token
   */
  const scheduleTokenRefresh = useCallback(
    (accessToken) => {
      // Cancel any existing timer
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }

      // Parse the token to get expiration time
      const payload = parseJWT(accessToken);
      if (!payload?.exp) {
        return;
      }

      // Calculate time until expiration
      const expirationMs = payload.exp * 1000;
      const nowMs = Date.now();
      const timeUntilExpiration = expirationMs - nowMs;

      // Schedule refresh 1 minute before expiration
      const refreshDelay = Math.max(
        timeUntilExpiration - REFRESH_THRESHOLD_MS,
        0 // Don't use negative delay
      );

      // Set up the refresh timer
      const timer = setTimeout(async () => {
        const success = await refreshAccessToken();

        if (success) {
          // Schedule the next refresh
          const { accessToken: newToken } = getStoredTokens();
          if (newToken) {
            scheduleTokenRefresh(newToken);
          }
        }
      }, refreshDelay);

      setRefreshTimer(timer);
    },
    [refreshTimer, refreshAccessToken]
  );

  // ==========================================================================
  // AUTHENTICATION METHODS
  // ==========================================================================

  /**
   * Logs in a user with username/email and password.
   *
   * @param {string} usernameOrEmail - Username or email
   * @param {string} password - User's password
   * @returns {Promise<Object>} Result with success boolean and user/error
   *
   * @example
   * const result = await login('john@example.com', 'password123');
   * if (result.success) {
   *   result.user.email;
   * } else {
   *   result.error;
   * }
   */
  const login = useCallback(
    async (usernameOrEmail, password) => {
      setIsLoading(true);
      setError(null);

      try {
        // Call login endpoint
        const response = await api.post('/auth/login', {
          usernameOrEmail,
          password,
        });

        // Check for successful response
        if (response.data?.success) {
          const { user: userData, accessToken, refreshToken } = response.data;

          // Store tokens
          storeTokens(accessToken, refreshToken);

          // Update user state
          setUser(userData);

          // Schedule token refresh
          scheduleTokenRefresh(accessToken);

          return { success: true, user: userData };
        }

        // Server returned success: false
        const errorMessage = response.data?.error || 'Login failed';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } catch (err) {
        // Request failed
        const errorMessage = err.response?.data?.error || err.message || 'Login failed';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsLoading(false);
      }
    },
    [scheduleTokenRefresh]
  );

  /**
   * Logs out the current user.
   *
   * This function:
   * 1. Calls the logout endpoint to revoke refresh token
   * 2. Clears stored tokens
   * 3. Clears user state
   * 4. Cancels scheduled token refresh
   *
   * @returns {Promise<void>}
   */
  const logout = useCallback(async () => {
    setIsLoading(true);

    try {
      // Get refresh token for revocation
      const { refreshToken } = getStoredTokens();

      // Call logout endpoint (if we have a token)
      if (refreshToken) {
        await api.post('/auth/logout', { refreshToken });
      }
    } catch {
      // Logout endpoint failed - that's okay, we'll clear local state anyway
    } finally {
      // Cancel scheduled token refresh
      if (refreshTimer) {
        clearTimeout(refreshTimer);
        setRefreshTimer(null);
      }

      // Clear stored tokens
      clearStoredTokens();

      // Clear user state
      setUser(null);
      setError(null);
      setIsLoading(false);

      // Navigate to login page to clear any cached redirect state
      // This prevents the next user from being redirected to pages they don't have access to
      // Using window.location ensures a clean page load with no navigation state
      window.location.href = '/login';
    }
  }, [refreshTimer]);

  /**
   * Logs out from all devices.
   *
   * Revokes all refresh tokens for the current user,
   * forcing re-authentication on all devices.
   *
   * @returns {Promise<Object>} Result with success boolean
   */
  const logoutAllDevices = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await api.post('/auth/logout-all');

      if (response.data?.success) {
      }

      // Logout locally as well
      await logout();

      return { success: true };
    } catch (err) {
      // Still logout locally
      await logout();
      return { success: false, error: err.message };
    }
  }, [logout]);

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================

  /**
   * Validates existing tokens and restores session on mount.
   *
   * Called once when the component mounts to check if the user
   * has a valid session from a previous visit.
   */
  useEffect(() => {
    async function initializeAuth() {
      try {
        const { accessToken, refreshToken } = getStoredTokens();

        // No tokens stored - user is not logged in
        if (!accessToken && !refreshToken) {
          setIsLoading(false);
          return;
        }

        // Check if access token is expired
        if (accessToken && !isTokenExpired(accessToken)) {
          // Access token is still valid - verify with server
          try {
            const response = await api.get('/auth/me');

            if (response.data?.success && response.data?.user) {
              setUser(response.data.user);
              scheduleTokenRefresh(accessToken);
              setIsLoading(false);
              return;
            }
          } catch {
            // Token verification failed - try refresh
          }
        }

        // Access token expired or verification failed - try refresh
        if (refreshToken) {
          const refreshSuccess = await refreshAccessToken();

          if (refreshSuccess) {
            // Get fresh user data
            try {
              const response = await api.get('/auth/me');
              if (response.data?.success && response.data?.user) {
                setUser(response.data.user);
                const { accessToken: newToken } = getStoredTokens();
                if (newToken) {
                  scheduleTokenRefresh(newToken);
                }
              }
            } catch {
              clearStoredTokens();
            }
          } else {
            // Refresh failed - user needs to login again
            clearStoredTokens();
          }
        } else {
          // No refresh token - clear stale access token
          clearStoredTokens();
        }
      } catch {
        clearStoredTokens();
      } finally {
        setIsLoading(false);
      }
    }

    initializeAuth();

    // Cleanup: Cancel refresh timer on unmount
    return () => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // ==========================================================================
  // CONTEXT VALUE
  // ==========================================================================

  /**
   * Context value provided to consumers.
   * Memoized to prevent unnecessary re-renders.
   */
  const value = useMemo(
    () => ({
      // State
      user,
      isAuthenticated: !!user,
      isLoading,
      error,

      // Authentication methods
      login,
      logout,
      logoutAllDevices,

      // Token management
      refreshAccessToken,

      // Helper to check roles
      hasRole: (requiredRoles) => {
        if (!user) return false;
        if (typeof requiredRoles === 'string') {
          return user.role === requiredRoles;
        }
        return requiredRoles.includes(user.role);
      },

      // Check if user is at least a certain role level
      hasMinRole: (minRole) => {
        if (!user) return false;
        const roleHierarchy = ['end_user', 'org_admin', 'admin_sales', 'super_admin'];
        const userLevel = roleHierarchy.indexOf(user.role);
        const requiredLevel = roleHierarchy.indexOf(minRole);
        return userLevel >= requiredLevel;
      },
    }),
    [user, isLoading, error, login, logout, logoutAllDevices, refreshAccessToken]
  );

  // ==========================================================================
  // RENDER
  // ==========================================================================

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// PropTypes for AuthProvider
AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// =============================================================================
// CUSTOM HOOK
// =============================================================================

/**
 * Hook to access the authentication context.
 *
 * This is the primary way components access auth state and methods.
 * Must be used within an AuthProvider.
 *
 * @returns {Object} Auth context value
 * @throws {Error} If used outside of AuthProvider
 *
 * @example
 * function MyComponent() {
 *   const { user, isAuthenticated, login, logout } = useAuth();
 *
 *   if (isAuthenticated) {
 *     return (
 *       <div>
 *         <p>Welcome, {user.firstName}!</p>
 *         <button onClick={logout}>Logout</button>
 *       </div>
 *     );
 *   }
 *
 *   return <button onClick={() => login('user', 'pass')}>Login</button>;
 * }
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);

  // Guard clause: Must be used within AuthProvider
  if (!context) {
    throw new Error(
      'useAuth must be used within an AuthProvider. ' +
        'Make sure your component is wrapped in <AuthProvider>.'
    );
  }

  return context;
}

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default AuthContext;
